/*
  Vemica bundle discount sync.
  Draft theme 164723851506.

  Keeps the Shopify discount code in step with the cart, in both directions,
  however the cart was changed - the bundle builder, a product page, the FlexyPe
  drawer, or an upsell tile.

      3-4 items    ->  5%  VEMICA-BUNDLE5
      5-9 items    -> 10%  VEMICA-BUNDLE10
      10-15 items  -> 15%  VEMICA-BUNDLE15
      fewer than 3 -> no discount, and any of our codes still applied is removed

  The ladder is NOT defined here. It comes from snippets/vemica-bundle-bands
  via window.VEMICA_BUNDLE_BANDS - see readBands().

  WHICH QUANTITY DRIVES THE BAND
  TOTAL CART QUANTITY (cart.item_count). Nothing else.

  LOADED SITE-WIDE  (changed 2026-08-11)
  This is loaded from layout/theme.liquid, so it runs on every storefront
  template. It used to be loaded from snippets/buy-buttons.liquid behind
  `template.name == 'product'`, and that was a real bug, not a limitation:

    - 4 items on a product page  -> VEMICA-BUNDLE5, Rs. 376.20, correct
    - navigate to the homepage   -> this script is not on the page
    - 4 -> 5 -> 6 in the drawer  -> still VEMICA-BUNDLE5, Rs. 564.30 at 6 items
    - return to any product page -> re-bands to VEMICA-BUNDLE10, Rs. 534.60

  Shopify itself held the wrong code, so the customer would have been charged
  the wrong amount; FlexyPe was displaying that cart faithfully. Carts built
  entirely from collection pages got no discount at all. Do not move this back
  behind a template condition.

  THE BUNDLE-TAG GATE WAS REMOVED  (2026-08-11, at the merchant's instruction)
  This used to require at least one line carrying the _vemica_bundle property
  before any discount was applied, so that a cart assembled without the bundle
  builder got nothing. That gate is gone: the discount is now decided purely by
  how many items are in the cart.

  CONSEQUENCE, ACCEPTED DELIBERATELY: every cart of 3 or more items now receives
  5-15% off, whether or not the customer ever opened the bundle builder. Adding
  three items from product pages is enough. This is effectively a store-wide
  quantity discount delivered by JavaScript, not a bundle-only offer.

  Two things follow from that, for whoever reads this next:
    1. The _vemica_bundle line property is still written by the builder, because
       it is useful for identifying bundle orders in admin. It no longer affects
       pricing in any way.
    2. With the gate gone, the original reason for using discount CODES rather
       than Shopify AUTOMATIC discounts has disappeared. Three automatic
       discounts with quantity minimums 3 / 5 / 10 would produce the same
       outcome server-side, with no JavaScript on any page, no session-cookie
       staleness, and no FlexyPe refresh lag. Worth revisiting.

  REMOVING A STALE CODE
  The applied discount is SERVER-SIDE cart state, not client state. Proven on
  this store 2026-08-10:

    - deleting the discount_code cookie from document.cookie  -> discount SURVIVED
    - deleting it at the network layer, zero cookies left     -> discount SURVIVED
    - GET /discount/            (empty code)                  -> 404, no change
    - GET /discount/<bogus>                                   -> 200, old code SURVIVED
    - a valid-but-unqualifying code (min quantity 9999)       -> old code SURVIVED
    - POST /cart/update.js  { discount: '' }                  -> CLEARED

  CAVEAT: `discount` is NOT in Shopify's documented AJAX Cart API for
  /cart/update.js, which lists updates, note and attributes. It is a legacy
  parameter that still works. The response - the updated cart - is therefore
  checked rather than assumed, and a failure to clear is logged as a warning.

  KNOWN LIMITATION - FlexyPe's drawer can lag one interaction at a tier boundary
  FlexyPe renders from the /cart/change.js response IT fetched, which still
  carries the OLD code. We apply the new code a moment later, so Shopify becomes
  correct but FlexyPe's display may not follow until the next interaction or a
  reload. This is cosmetic - Shopify's total_price is the charged amount.

  Calling <flexy-cart-element>.render() was tried to force a repaint and it
  REMOVED THE ELEMENT FROM THE DOM - the drawer became unreachable for the rest
  of the session. It must not be reintroduced.

  UNRELATED TO US: the drawer shows a "5% Off" badge inside its "People bought
  these together" upsell strip even on a 2-item cart with no discount at all.
  That is FlexyPe promo copy from their dashboard, not a cart line and not a
  real discount. Do not chase it from the theme.

  NOTE ON VERIFYING WHICH BUILD IS LIVE
  Shopify MINIFIES theme JS on the CDN - it is not just comment stripping. The
  served file here is ~4.6 KB against ~12 KB of source, on 2 lines, with quotes
  normalised to double and `false` written `!1`. Local names DO survive, so grep
  the served asset for bare identifiers (`bandFor`, `cartQty`, `item_count`) and
  never for comment text, spaced expressions, or a particular quote style - a
  regex like /bandFor\(cartQty, bands\)/ will not match the minified output.

  WHAT THIS WILL NOT TOUCH
  A discount code that is not one of ours. If a customer has applied their own
  coupon and it is discounting the cart, this leaves it alone.

  SCHEDULING
  Reconciles are debounced, and when throttled or already in flight they are
  RESCHEDULED, never dropped.
*/
(function () {
  'use strict';

  var CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/i;
  var OUR_CODE_PATTERN = /^VEMICA-BUNDLE/i;
  var DEBOUNCE_MS = 450;
  var MIN_GAP_MS = 900;

  var timer = null;
  var busy = false;
  var internal = false; // suppresses self-triggered reconciles
  var lastRun = 0;
  var warnedDrift = false;

  function root() {
    return window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
  }

  function normalise(list) {
    return (list || [])
      .filter(function (b) { return b && typeof b.min === 'number' && b.code; })
      .map(function (b) { return { min: b.min, pct: b.pct, code: b.code }; })
      .sort(function (a, b) { return a.min - b.min; });
  }

  /*
    The ladder, in priority order:

      1. window.VEMICA_BUNDLE_BANDS, from snippets/vemica-bundle-bands, rendered
         by layout/theme.liquid on EVERY template. This is the source of truth.
      2. the <vemica-bundle-builder> element's data-vbb-bands, product pages
         only. Kept as a fallback so the reconciler still works if the layout
         render is ever removed.

    The builder keeps its own copy because it needs the same numbers for the UI
    text it paints. When both are present they are compared, and a mismatch is
    logged once - drift between the two would otherwise be invisible.
  */
  function readBands() {
    var fromGlobal = null;
    try {
      if (window.VEMICA_BUNDLE_BANDS && window.VEMICA_BUNDLE_BANDS.length) {
        fromGlobal = normalise(window.VEMICA_BUNDLE_BANDS);
      }
    } catch (e) {}

    var fromElement = null;
    var el = document.querySelector('vemica-bundle-builder');
    if (el) {
      try {
        var raw = JSON.parse(el.getAttribute('data-vbb-bands') || 'null');
        if (raw && raw.length) fromElement = normalise(raw);
      } catch (e) {}
    }

    if (fromGlobal && fromElement && !warnedDrift) {
      try {
        if (JSON.stringify(fromGlobal) !== JSON.stringify(fromElement)) {
          warnedDrift = true;
          if (window.console && window.console.warn) {
            window.console.warn(
              '[vemica-bundle] the ladder in snippets/vemica-bundle-bands does not match ' +
              'the one on <vemica-bundle-builder>. The cart will follow vemica-bundle-bands, ' +
              'so the prices shown in the builder UI may disagree with what is charged. ' +
              'Reconcile the two snippets.',
              { bands: fromGlobal, builder: fromElement }
            );
          }
        }
      } catch (e) {}
    }

    return fromGlobal || fromElement || [];
  }

  function bandFor(quantity, bands) {
    var found = null;
    for (var i = 0; i < bands.length; i++) {
      if (quantity >= bands[i].min) found = bands[i];
    }
    return found;
  }

  function isOurCode(title, bandCodes) {
    if (!title) return false;
    if (bandCodes.indexOf(title) !== -1) return true;
    return OUR_CODE_PATTERN.test(title);
  }

  /*
    Called ONLY after a discount change has actually succeeded.
    Do NOT call <flexy-cart-element>.render() from here - see the header.
  */
  function announce(cart) {
    try {
      document.dispatchEvent(new CustomEvent('vemica:bundle-discount-synced', {
        bubbles: true,
        detail: { cart: cart },
      }));
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: cart } }));
    } catch (e) {}
    try {
      if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'vemica-bundle-discount-sync', cart: cart });
      }
    } catch (e) {}
  }

  function retry(ms) {
    clearTimeout(timer);
    timer = setTimeout(reconcile, ms);
  }

  function schedule() {
    retry(DEBOUNCE_MS);
  }

  function applyCode(code, verifyAfter) {
    return fetch('/discount/' + encodeURIComponent(code) + '?redirect=/cart.js', {
      headers: { Accept: 'application/json' },
    })
      .then(function (r) { return r.json(); })
      .then(function (fresh) {
        busy = false;
        announce(fresh);
        if (verifyAfter) retry(MIN_GAP_MS + 150);
        return fresh;
      })
      .catch(function () { busy = false; });
  }

  /*
    Removes the discount from the cart. Sends ONLY the discount field, so cart
    note, cart attributes, line-item properties and quantities are untouched -
    verified before and after on a cart carrying a note, custom attributes,
    FlexyPe's fp_id and extra line properties.
  */
  function clearDiscount() {
    internal = true;
    return fetch(root() + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: '' }),
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        internal = false;
        busy = false;

        if (cart && typeof cart.total_discount === 'number' && cart.total_discount > 0) {
          try {
            if (window.console && window.console.warn) {
              window.console.warn(
                '[vemica-bundle] clearing the stale discount had no effect; ' +
                'POST /cart/update.js {discount:""} may no longer be supported. ' +
                'Cart still discounted by ' + cart.total_discount
              );
            }
          } catch (e) {}
        }

        announce(cart);
        return cart;
      })
      .catch(function () {
        internal = false;
        busy = false;
      });
  }

  function reconcile() {
    if (busy) { retry(MIN_GAP_MS); return; }

    var bands = readBands();
    if (!bands.length) return;

    var since = Date.now() - lastRun;
    if (since < MIN_GAP_MS) { retry(MIN_GAP_MS - since + 50); return; }

    var bandCodes = bands.map(function (b) { return b.code; });

    lastRun = Date.now();
    busy = true;

    fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var applied = ((cart && cart.cart_level_discount_applications) || [])
          .map(function (d) { return d.title; });

        var ours = applied.filter(function (t) { return isOurCode(t, bandCodes); });
        var foreign = applied.filter(function (t) { return !isOurCode(t, bandCodes); });

        // Total cart quantity is the ONLY input. No bundle-tag gate - see header.
        var cartQty = typeof cart.item_count === 'number' ? cart.item_count : 0;
        var want = bandFor(cartQty, bands);

        // Someone else's coupon is doing the discounting - leave it be.
        if (foreign.length && cart.total_discount > 0) {
          busy = false;
          return null;
        }

        if (want) {
          var alreadyRight =
            applied.length === 1 && applied[0] === want.code && cart.total_discount > 0;
          if (alreadyRight) {
            busy = false;
            return null;
          }
          return applyCode(want.code, true);
        }

        // Fewer than the lowest band needs: no discount should be active.
        if (ours.length) {
          return clearDiscount();
        }

        busy = false;
        return null;
      })
      .catch(function () {
        busy = false;
      });
  }

  /* ---------------------------------------------- observe cart mutations */

  var nativeFetch = window.fetch;
  if (typeof nativeFetch === 'function') {
    window.fetch = function (input) {
      var url = '';
      try {
        url = typeof input === 'string' ? input : (input && input.url) || '';
      } catch (e) {}

      var promise = nativeFetch.apply(this, arguments);

      if (!internal && url && CART_MUTATION.test(url)) {
        try { promise.then(schedule, function () {}); } catch (e) {}
      }
      return promise;
    };
  }

  var nativeOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    try {
      if (!internal && url && CART_MUTATION.test(String(url))) {
        this.addEventListener('loadend', schedule);
      }
    } catch (e) {}
    return nativeOpen.apply(this, arguments);
  };

  function touchesCart(event) {
    try {
      var path = event.composedPath ? event.composedPath() : [];
      for (var i = 0; i < path.length; i++) {
        var node = path[i];
        if (!node || !node.tagName) continue;
        var tag = node.tagName.toLowerCase();
        if (tag === 'flexy-cart-element' || tag === 'cart-drawer' || tag === 'cart-items') return true;
        var cls = node.className && node.className.toString ? node.className.toString() : '';
        if (/cart|flexy/i.test(cls)) return true;
      }
    } catch (e) {}
    return false;
  }

  document.addEventListener('click', function (event) {
    if (touchesCart(event)) schedule();
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }
})();
